/**
 * Web Push notification utilities
 */

import { useAuthStore } from '@/stores'

const API_BASE = import.meta.env.VITE_API_URL || ''

/**
 * Convert a base64 URL-encoded string to a Uint8Array
 * Required for VAPID applicationServerKey
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window
}

/**
 * Check if notification permission has been granted
 */
export function isPushPermissionGranted(): boolean {
  return Notification.permission === 'granted'
}

/**
 * Get the VAPID public key from the server
 */
async function getVapidPublicKey(): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE}/api/v1/notifications/vapid-public-key`)
    if (!response.ok) {
      console.error('Failed to get VAPID public key:', response.status)
      return null
    }
    const data = await response.json()
    return data.public_key
  } catch (error) {
    console.error('Error fetching VAPID public key:', error)
    return null
  }
}

export type PushSubscribeResult =
  | { success: true }
  | { success: false; reason: 'not_supported' | 'permission_denied' | 'vapid_unavailable' | 'sw_not_ready' | 'subscription_failed' | 'server_error' | 'no_auth'; message: string }

/**
 * Subscribe to push notifications.
 * Returns a result object with success/failure and reason.
 */
export async function subscribeToPush(): Promise<PushSubscribeResult> {
  if (!isPushSupported()) {
    return { success: false, reason: 'not_supported', message: 'Push notifications are not supported in this browser' }
  }

  try {
    // Step 1: Request browser permission
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return { success: false, reason: 'permission_denied', message: 'Notification permission was denied' }
    }

    // Step 2: Get VAPID public key from server
    const vapidPublicKey = await getVapidPublicKey()
    if (!vapidPublicKey) {
      return { success: false, reason: 'vapid_unavailable', message: 'Push notifications are not configured on the server' }
    }

    // Step 3: Wait for service worker to be ready (with timeout)
    // .ready resolves once the SW is installed + activated.
    // Unlike getRegistration() which resolves immediately (possibly before registration completes),
    // .ready waits for the SW lifecycle to finish — essential for pushManager.subscribe().
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 10_000)),
    ])
    if (!registration) {
      return { success: false, reason: 'sw_not_ready', message: 'Service worker not ready. Try refreshing the page.' }
    }

    // Step 4: Create push subscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })

    // Step 5: Send subscription to backend
    const token = useAuthStore.getState().token
    if (!token) {
      return { success: false, reason: 'no_auth', message: 'Not authenticated' }
    }

    const subscriptionJson = subscription.toJSON()
    const response = await fetch(`${API_BASE}/api/v1/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: subscriptionJson.endpoint,
        keys: {
          p256dh: subscriptionJson.keys?.p256dh,
          auth: subscriptionJson.keys?.auth,
        },
      }),
    })

    if (!response.ok) {
      return { success: false, reason: 'server_error', message: `Server returned ${response.status}` }
    }

    return { success: true }
  } catch (error) {
    console.error('Error subscribing to push:', error)
    return { success: false, reason: 'subscription_failed', message: String(error) }
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) {
    return false
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      return true
    }

    // Unsubscribe from browser
    await subscription.unsubscribe()

    // Remove from backend
    const token = useAuthStore.getState().token
    if (token) {
      await fetch(`${API_BASE}/api/v1/notifications/unsubscribe`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          endpoint: subscription.endpoint,
        }),
      })
    }

    return true
  } catch (error) {
    console.error('Error unsubscribing from push:', error)
    return false
  }
}

/**
 * Get current push subscription status
 */
export async function getPushSubscriptionStatus(): Promise<{
  supported: boolean
  permissionGranted: boolean
  subscribed: boolean
}> {
  const supported = isPushSupported()

  if (!supported) {
    return { supported: false, permissionGranted: false, subscribed: false }
  }

  const permissionGranted = isPushPermissionGranted()

  let subscribed = false
  try {
    // Use getRegistration() — resolves immediately with undefined if no SW registered.
    // Unlike .ready which hangs forever waiting for a SW to be installed.
    const registration = await navigator.serviceWorker.getRegistration()
    if (registration) {
      const subscription = await registration.pushManager.getSubscription()
      subscribed = !!subscription
    }
  } catch {
    // Service worker not available or error
  }

  return { supported, permissionGranted, subscribed }
}
